import React, { Fragment, useState, useContext } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CustomizerContext from "../_helper/Customizer";
import "./Signin.scss";

// ─── API ──────────────────────────────────────────────────────────────────────
import { loginApi } from "../api/api";

// ─── Logo ─────────────────────────────────────────────────────────────────────
import HorizontalLogo from "../assets/images/logo/Murasal_One_Horizontal_Logo_Transparent.png";

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const EyeOpen = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosed = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const LockIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────
const Signin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const { layoutURL } = useContext(CustomizerContext);

  // Get meeting ID from URL params or query params
  const { meetingId } = useParams();
  const [searchParams] = useSearchParams();
  const redirectMeetingId = searchParams.get("meetingId") || meetingId;

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const newErrors = {};
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = "Enter a valid email address";
    }
    if (!password) {
      newErrors.password = "Password is required";
    }
    return newErrors;
  };

  // ── Submit handler ────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const response = await loginApi({
        email: email.trim(),
        password,
        deviceName: "Admin Portal Web",
      });

      const { accessToken, refreshToken, user, matrix } = response.data;

      // Persist auth data
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("login", JSON.stringify(true));
      localStorage.setItem("authenticated", JSON.stringify(true));
      localStorage.setItem("authUser", JSON.stringify(user));

      if (matrix) {
        localStorage.setItem("matrixCredentials", JSON.stringify(matrix));
      }

      if (!rememberMe) {
        sessionStorage.setItem("sessionOnly", "true");
      }

      if (redirectMeetingId) {
        sessionStorage.setItem("pendingMeetingId", redirectMeetingId);
      }

      toast.success(`Welcome back, ${user?.fullName ?? "User"}!`);

      setTimeout(() => {
        const role = user?.role?.toUpperCase();

        // If user came from a meeting invitation link,
        // always open the meeting regardless of role
        if (redirectMeetingId) {
          navigate(
            `${process.env.PUBLIC_URL}/mursal-meetings/${redirectMeetingId}`,
          );
          return;
        }

        // Roles that should go to dashboard
        const dashboardRoles = [
          "ADMIN",
          "SUPER_ADMIN",
          "OPERATION_ADMIN",
          "SUPPORT_ADMIN",
          "GROUP_ADMIN",
          "AUDITOR",
          "VIEWER",
        ];

        if (dashboardRoles.includes(role)) {
          navigate(`${process.env.PUBLIC_URL}/dashboard/${layoutURL}`);
        } else {
          // USER role
          navigate(`${process.env.PUBLIC_URL}/mursal-meetings`);
        }
      }, 800);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Login failed. Please check your credentials.";

      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // ── Field change helpers ──────────────────────────────────────────────────
  const onEmailChange = (e) => {
    setEmail(e.target.value);
    if (errors.email) setErrors((p) => ({ ...p, email: "" }));
  };

  const onPasswordChange = (e) => {
    setPassword(e.target.value);
    if (errors.password) setErrors((p) => ({ ...p, password: "" }));
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Fragment>
      <div className="signin-page">
        {/* Floating background dots */}
        <div className="signin-bg-dots">
          <span />
          <span />
          <span />
          <span />
          <span />
        </div>

        <div className="signin-card">
          {/* Logo */}
          <div className="signin-logo-wrap">
            <img src={HorizontalLogo} alt="Murasal One" />
          </div>

          {/* Accent line */}
          <div className="signin-accent" />

          <h2 className="signin-heading">Welcome Back</h2>
          <p className="signin-subheading">
            {redirectMeetingId
              ? "Sign in to join your meeting"
              : "Sign in to the Admin Portal"}
          </p>

          <form className="signin-form" onSubmit={handleSubmit} noValidate>
            {/* ── Email field ── */}
            <div className="signin-field">
              <label htmlFor="email">Email</label>
              <div className="signin-input-wrap">
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={onEmailChange}
                  placeholder="Enter your email"
                  className={`signin-input${errors.email ? " has-error" : ""}`}
                />
              </div>
              {errors.email && (
                <div className="signin-error-text">{errors.email}</div>
              )}
            </div>

            {/* ── Password field ── */}
            <div className="signin-field">
              <label htmlFor="password">Password</label>
              <div className="signin-input-wrap">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={onPasswordChange}
                  placeholder="Enter your password"
                  className={`signin-input has-eye${errors.password ? " has-error" : ""}`}
                />
                <button
                  type="button"
                  className="signin-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOpen /> : <EyeClosed />}
                </button>
              </div>
              {errors.password && (
                <div className="signin-error-text">{errors.password}</div>
              )}
            </div>

            {/* ── Remember me + Forgot ── */}
            <div className="signin-row">
              <label className="signin-check-label">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                Remember me
              </label>
              <a href="#forgot" className="signin-forgot">
                Forgot password?
              </a>
            </div>

            {/* ── Submit ── */}
            <button type="submit" className="signin-btn" disabled={loading}>
              {loading && <span className="signin-spinner" />}
              {loading
                ? "Signing in…"
                : redirectMeetingId
                  ? "Sign In & Join Meeting"
                  : "Sign In"}
            </button>
          </form>

          {/* Footer */}
          <div className="signin-footer">
            <LockIcon />
            Secure · Encrypted · Murasil One Admin v1.0
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        toastStyle={{ fontFamily: "'Inter', sans-serif", fontSize: 14 }}
      />
    </Fragment>
  );
};

export default Signin;
